import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { GitQuery } from '@y42/git';
import { OrgUIModel } from '@y42/org/models';
import { OrgQuery } from '@y42/org/state';
import { UserQuery } from '@y42/user/state';
import { isNotNullish } from '@y42/util/nullish';
import { combineLatest, filter, map, Observable, pairwise, switchMap } from 'rxjs';
import {
  DatabaseType,
  DatabaseTypeToLabel,
  OrgSpaceActiveAggregationData,
  OrgSpaceState,
  OrgSpaceStoreModel,
  OrgSpaceUIModel,
} from '../models';
import { OrgSpaceStore } from './org-space.store';

@Injectable({ providedIn: 'root' })
export class OrgSpaceQuery extends QueryEntity<OrgSpaceState> {
  constructor(
    readonly orgSpaceStore: OrgSpaceStore,
    private readonly userQuery: UserQuery,
    private readonly orgQuery: OrgQuery,
    private readonly gitQuery: GitQuery,
  ) {
    super(orgSpaceStore);
  }

  readonly loading$ = this.selectLoading();

  readonly activeOrgSpaces$: Observable<OrgSpaceUIModel[]> = combineLatest([
    this.orgQuery.activeOrg$,
    this.select(),
  ]).pipe(
    map(([org]) => org.id),
    switchMap((orgId) => this.selectOrgSpaces(orgId)),
  );

  readonly active$: Observable<OrgSpaceUIModel | null> = this.selectActive().pipe(map((active) => active ?? null));
  readonly activeOrgSpaceHasChanged$ = this.active$.pipe(
    pairwise(),
    map(([a, b]) => a && b && a.id !== b.id),
    filter(Boolean),
  );

  readonly hasActiveOrgSpaceBigQueryDatabase$ = this.active$.pipe(
    map((activeSpace) => activeSpace?.dataWarehouse.database.type === DatabaseType.BigQuery),
  );

  readonly hasActiveOrgSpaceSnowflakeDatabase$ = this.active$.pipe(
    map((activeSpace) => activeSpace?.dataWarehouse.database.type === DatabaseType.Snowflake),
  );

  readonly aggregatedData$: Observable<OrgSpaceActiveAggregationData> = combineLatest([
    this.userQuery.activeUserId$,
    this.orgQuery.selectActive(),
    this.selectActiveId(),
    this.gitQuery.activeBranchName$,
  ]).pipe(
    map(([userId, org, spaceId, branch]) => {
      return {
        spaceId: spaceId ?? '',
        orgId: org?.id ?? -1,
        orgSlug: org?.slug ?? '',
        branch: branch ?? '',
        userId: Number(userId),
      };
    }),
  );

  readonly activeDatabaseDetails$ = this.active$.pipe(
    map((activeSpace) => {
      const activeDB = activeSpace?.dataWarehouse.database;
      if (!activeDB) {
        return null;
      }

      const dbType = activeDB.type;
      return {
        type: dbType,
        label: DatabaseTypeToLabel[dbType],
        data: activeDB,
      };
    }),
  );

  areOrgSpacesLoaded(orgId: OrgUIModel['id']): boolean {
    return !!this.getValue().spacesByOrg[orgId];
  }

  selectOrgSpaces(orgId: OrgUIModel['id']): Observable<OrgSpaceUIModel[]> {
    return this.select((st) => st.spacesByOrg).pipe(map((spacesByOrg) => this.hydrateOrgSpaces(spacesByOrg, orgId)));
  }

  getOrgSpaces(orgId: OrgUIModel['id']): OrgSpaceUIModel[] {
    return this.hydrateOrgSpaces(this.getValue().spacesByOrg, orgId);
  }

  getOrgSpace(orgId: OrgUIModel['id'], spaceId: OrgSpaceStoreModel['id']): OrgSpaceUIModel | null {
    return this.getOrgSpaces(orgId).find((space) => space.id === spaceId) || null;
  }

  /**
   * @deprecated UX3-only: please use `FeatureContextService` from '@y42/feature-v2/context' instead
   */
  aggregatedData(): OrgSpaceActiveAggregationData {
    const spaceId = this.getActiveId() ?? '';
    const orgId = this.orgQuery.getActiveId() ?? -1;
    const orgSlug = this.orgQuery.getActive()?.slug ?? '';
    const branch = this.gitQuery.getActiveBranchName() ?? '';
    const userId = this.userQuery.getActiveUserId() ?? -1;
    return {
      spaceId,
      orgId,
      orgSlug,
      branch,
      userId,
    };
  }

  private hydrateOrgSpaces(
    spacesByOrg: Record<OrgUIModel['id'], Array<OrgSpaceStoreModel['id']>>,
    orgId: OrgUIModel['id'],
  ): OrgSpaceStoreModel[] {
    const spaceIds = spacesByOrg[orgId] || [];
    return spaceIds.map((spaceId) => this.getEntity(spaceId)).filter(isNotNullish);
  }
}
